import type { InputState } from './InputSystem';
import { WALL_DEFS } from '../wallDefs';

const ARENA_BOUND = 95;
const TANK_RADIUS = 0.9;

/**
 * Resolve a circle against an AABB. Returns pushed-out position or null if clear.
 * Mirrors resolveCircleAABB in server/src/rooms/ArenaRoom.ts.
 */
function resolveCircleAABB(
  cx: number, cz: number, radius: number,
  wx: number, wz: number, whw: number, whd: number,
): { nx: number; nz: number } | null {
  const closestX = Math.max(wx - whw, Math.min(wx + whw, cx));
  const closestZ = Math.max(wz - whd, Math.min(wz + whd, cz));
  const dx = cx - closestX;
  const dz = cz - closestZ;
  const distSq = dx * dx + dz * dz;
  if (distSq >= radius * radius) return null;
  if (distSq < 1e-8) {
    const overlapX = whw + radius - Math.abs(cx - wx);
    const overlapZ = whd + radius - Math.abs(cz - wz);
    if (overlapX <= overlapZ) {
      return { nx: cx + (cx >= wx ? overlapX : -overlapX), nz: cz };
    }
    return { nx: cx, nz: cz + (cz >= wz ? overlapZ : -overlapZ) };
  }
  const dist = Math.sqrt(distSq);
  const push = radius - dist;
  return { nx: cx + (dx / dist) * push, nz: cz + (dz / dist) * push };
}

/**
 * Owns all local player movement state: position, facing rotation, move/turn speed,
 * and the pending server-correction vector. The game loop calls update() once per
 * frame and reads x/z/rotation for rendering and network sends.
 *
 * Camera management is intentionally NOT here — CameraSystem stays separate so pitch
 * and free-orbit can be controlled independently.
 */
export class PlayerController {
  x = 0;
  z = 0;

  /**
   * Direction the tank is facing, in radians.
   * With this value, the tank's barrel points toward (sin(rotation), 0, -cos(rotation))
   * in world space. The camera yaw should be set to this same value so it sits
   * directly behind the tank.
   */
  rotation = 0;

  moveSpeed = 6;   // units/s — synced from server after movement upgrades
  turnSpeed = 2.5; // rad/s  — synced from server after turn-speed stat upgrades

  private corrX = 0; // pending server correction, drained each frame
  private corrZ = 0;

  /**
   * Advance the controller by one game-loop frame.
   *
   * @param input       Captured input for this frame.
   * @param dt          Delta-time in seconds.
   * @param locked      True when camera is locked behind the tank (default mode).
   *                    - locked=true : turn input rotates the tank; movement is tank-relative.
   *                    - locked=false: turn input rotates the camera only (handled outside);
   *                                   movement uses freeCamYaw so WASD stays camera-relative.
   * @param freeCamYaw  Current camera yaw — only used when locked=false.
   * @returns worldDX/worldDZ: normalised world-space movement vector this frame,
   *          moveLen: magnitude before normalisation (1 when a direction is held, 0 at rest).
   */
  update(
    input: InputState,
    dt: number,
    locked: boolean,
    freeCamYaw: number,
  ): { worldDX: number; worldDZ: number; moveLen: number } {

    // ── 1. Rotation ──────────────────────────────────────────
    // Only update tank facing in locked mode. In free mode the camera orbits
    // independently and the tank body stays at its last rotation.
    if (locked) {
      this.rotation += input.turn * this.turnSpeed * dt;
    }

    // ── 2. Server-correction drain ────────────────────────────
    // Applied every frame with a rate-8 exponential lerp (~375 ms to settle).
    // This is separate from movement so position prediction stays clean.
    if (this.corrX !== 0 || this.corrZ !== 0) {
      const k = 1 - Math.exp(-8 * dt);
      this.x    += this.corrX * k;
      this.z    += this.corrZ * k;
      this.corrX *= 1 - k;
      this.corrZ *= 1 - k;
      if (this.corrX * this.corrX + this.corrZ * this.corrZ < 0.001) {
        this.corrX = 0;
        this.corrZ = 0;
      }
    }

    // ── 3. WASD → world-space movement ───────────────────────
    // In locked mode, movement is relative to the tank's own facing direction,
    // so W always means "go where the barrel is pointing."
    // In free mode, movement is relative to the camera yaw (same convention
    // the old code used).
    const yaw = locked ? this.rotation : freeCamYaw;
    const sy = Math.sin(yaw);
    const cy = Math.cos(yaw);

    // dz = -1 when W is held (forward); dx = ±1 for A/D strafe.
    // The transform below rotates the camera/tank-local input axes to world XZ.
    let worldDX = -input.dz * sy + input.dx * cy;
    let worldDZ =  input.dz * cy + input.dx * sy;

    const moveLen = Math.sqrt(worldDX * worldDX + worldDZ * worldDZ);
    if (moveLen > 1) { worldDX /= moveLen; worldDZ /= moveLen; }

    // ── 4. Apply velocity & clamp ─────────────────────────────
    this.x += worldDX * this.moveSpeed * dt;
    this.z += worldDZ * this.moveSpeed * dt;
    this.x = Math.max(-ARENA_BOUND, Math.min(ARENA_BOUND, this.x));
    this.z = Math.max(-ARENA_BOUND, Math.min(ARENA_BOUND, this.z));

    // ── 5. Interior wall collision ────────────────────────────
    for (const w of WALL_DEFS) {
      const r = resolveCircleAABB(this.x, this.z, TANK_RADIUS, w.x, w.z, w.hw, w.hd);
      if (r) { this.x = r.nx; this.z = r.nz; }
    }

    return { worldDX, worldDZ, moveLen };
  }

  /**
   * Hard-snap the controller to a new position and zero any pending correction.
   * Call this on respawn so the tank doesn't drift from a stale correction vector.
   */
  resetTo(x: number, z: number): void {
    this.x     = x;
    this.z     = z;
    this.corrX = 0;
    this.corrZ = 0;
  }

  /**
   * Called on every server state sync for the local player.
   * Large deviations (respawn, hard desync) snap immediately.
   * Smaller ones are queued into corrX/corrZ and drained by update().
   */
  applyServerCorrection(authX: number, authZ: number): void {
    const errX  = authX - this.x;
    const errZ  = authZ - this.z;
    const errSq = errX * errX + errZ * errZ;

    if (errSq > 100) {
      // > 10 units: respawn / teleport — snap immediately
      this.x     = authX;
      this.z     = authZ;
      this.corrX = 0;
      this.corrZ = 0;
    } else if (errSq > 2.25) {
      // > 1.5 units: genuine desync — smooth correction over ~375 ms
      this.corrX = errX;
      this.corrZ = errZ;
    }
    // ≤ 1.5 units: normal Wi-Fi prediction drift — ignore
  }
}
